import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  LuArrowLeft, LuInfo, LuScanLine, LuCircleCheck, LuFileText, LuImage, LuPackagePlus, LuRefreshCw,
  LuSave, LuSparkles, LuTrash2, LuTriangleAlert, LuUpload, LuX,
} from 'react-icons/lu';
import API, { SERVER_URL } from '../../api/axios';
import { Badge, Button, Card, EmptyState, Loader } from '../ui';
import { fmt, fmtF } from '../../theme';
import './ApprovisionnementIA.css';

const STATUTS = {
  analyse:    { label: 'Analyse en cours', tone: 'info' },
  a_verifier: { label: 'À vérifier',       tone: 'warn' },
  validee:    { label: 'Validée',          tone: 'success' },
  erreur:     { label: 'Erreur',           tone: 'danger' },
};
const ACTIONS = [
  { id: 'nouveau',  label: 'Nouveau produit' },
  { id: 'existant', label: 'Produit existant' },
  { id: 'ignorer',  label: 'Ignorer' },
];

const msgErreur = e => e?.response?.data?.message?.replace(/^❌\s*/, '') || 'Erreur réseau';
const urlUpload = chemin => (chemin ? `${SERVER_URL}/uploads/${chemin}` : null);
const dateFr = d => (d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '');

/** Coût d'achat unitaire en FCFA (même calcul que le serveur : frais de port au prorata). */
function calculerCouts(taux, fraisPort, lignes) {
  const t = Number(taux) || 1;
  const gardees = lignes.filter(l => l.action !== 'ignorer');
  const montant = l => (Number(l.prix_unitaire) || 0) * (Number(l.quantite) || 0);
  const total = gardees.reduce((s, l) => s + montant(l), 0);
  const port = (Number(fraisPort) || 0) * t;
  const couts = {};
  for (const l of gardees) {
    const q = Number(l.quantite) || 0;
    couts[l.id] = Math.round((Number(l.prix_unitaire) || 0) * t + (total > 0 && q > 0 ? (port * montant(l) / total) / q : 0));
  }
  return { couts, totalDevise: total };
}

// ════════════════════════════════════════════════════════════════
export default function ApprovisionnementIA({ etablissements = [], etablissementId, onStockChange }) {
  const [factureId, setFactureId] = useState(null);
  const [toast, setToast] = useState('');
  const notifier = useCallback(t => { setToast(t); setTimeout(() => setToast(''), 4000); }, []);

  return (
    <div className="aia">
      {toast && <div role="status" className={`aia-toast ${toast.startsWith('❌') ? 'is-err' : ''}`}>{toast.replace(/^(✅|❌)\s*/, '')}</div>}
      {factureId
        ? <Facture id={factureId} retour={() => setFactureId(null)} notifier={notifier} onStockChange={onStockChange} />
        : <Accueil etablissements={etablissements} etablissementId={etablissementId} ouvrir={setFactureId} notifier={notifier} />}
    </div>
  );
}

// ════════════════════════ ACCUEIL : import + historique ════════════════════════
function Accueil({ etablissements, etablissementId, ouvrir, notifier }) {
  const [fichiers, setFichiers] = useState([]);
  const [etab, setEtab] = useState(etablissementId || '');
  const [envoi, setEnvoi] = useState(false);
  const [survol, setSurvol] = useState(false);
  const [factures, setFactures] = useState(null);
  const input = useRef(null);

  useEffect(() => { if (!etab && etablissements[0]) setEtab(etablissements[0].id); }, [etablissements, etab]);

  const charger = useCallback(async () => {
    try {
      const r = await API.get('/factures');
      setFactures(r.data.factures || []);
    } catch (e) { notifier('❌ ' + msgErreur(e)); setFactures([]); }
  }, [notifier]);

  useEffect(() => {
    charger();
    const t = setInterval(charger, 8000);
    return () => clearInterval(t);
  }, [charger]);

  const ajouter = liste => {
    const ok = Array.from(liste || []).filter(f => /pdf|jpe?g|png|webp/i.test(f.type) || /\.(pdf|jpe?g|png|webp)$/i.test(f.name));
    if (ok.length < (liste?.length || 0)) notifier('❌ Formats acceptés : PDF, JPG, PNG, WEBP');
    setFichiers(prev => [...prev, ...ok].slice(0, 10));
  };

  const envoyer = async () => {
    if (!fichiers.length) return;
    setEnvoi(true);
    try {
      const fd = new FormData();
      fichiers.forEach(f => fd.append('fichiers', f));
      if (etab) fd.append('etablissement_id', etab);
      const r = await API.post('/factures', fd);
      setFichiers([]);
      ouvrir(r.data.id);
    } catch (e) { notifier('❌ ' + msgErreur(e)); }
    finally { setEnvoi(false); }
  };

  return (
    <>
      <Card style={{ marginBottom: 20 }}>
        <div className="aia-intro">
          <div className="aia-intro-icone"><LuScanLine aria-hidden /></div>
          <div>
            <h2 className="aia-titre">Nouvelle facture fournisseur</h2>
            <p className="aia-sub">Envoyez le PDF ou des photos de la facture : StockPro lit chaque article, reconnaît les produits déjà en stock et prépare l'approvisionnement. Vous vérifiez, puis vous validez.</p>
          </div>
        </div>

        {etablissements.length > 1 && (
          <label className="aia-champ" style={{ maxWidth: 360, marginBottom: 12 }}>
            <span>Établissement</span>
            <select className="aia-input" value={etab} onChange={e => setEtab(e.target.value)}>
              {etablissements.map(e => <option key={e.id} value={e.id}>{e.nom}</option>)}
            </select>
          </label>
        )}

        <div
          className={`aia-depot ${survol ? 'is-survol' : ''}`}
          onDragOver={e => { e.preventDefault(); setSurvol(true); }}
          onDragLeave={() => setSurvol(false)}
          onDrop={e => { e.preventDefault(); setSurvol(false); ajouter(e.dataTransfer.files); }}
          onClick={() => input.current?.click()}
          role="button" tabIndex={0}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') input.current?.click(); }}
        >
          <LuUpload aria-hidden className="aia-depot-icone" />
          <strong>Déposez la facture ici ou cliquez pour choisir</strong>
          <span className="aia-sub">PDF ou photos (JPG, PNG) — jusqu'à 10 fichiers, 20 Mo chacun. Sur téléphone, vous pouvez prendre la photo directement.</span>
          <input
            ref={input} type="file" multiple hidden
            accept="application/pdf,image/jpeg,image/png,image/webp"
            onChange={e => { ajouter(e.target.files); e.target.value = ''; }}
          />
        </div>

        {fichiers.length > 0 && (
          <ul className="aia-fichiers">
            {fichiers.map((f, i) => (
              <li key={i}>
                {/pdf/i.test(f.type) ? <LuFileText aria-hidden /> : <LuImage aria-hidden />}
                <span className="aia-fichier-nom">{f.name}</span>
                <span className="aia-sub">{(f.size / 1024 / 1024).toFixed(1)} Mo</span>
                <button className="aia-icone-btn" aria-label={`Retirer ${f.name}`} onClick={() => setFichiers(fichiers.filter((_, j) => j !== i))}><LuX aria-hidden /></button>
              </li>
            ))}
          </ul>
        )}

        <div className="aia-actions" style={{ marginTop: 14 }}>
          <Button icon={LuSparkles} disabled={!fichiers.length || envoi} onClick={envoyer}>
            {envoi ? 'Envoi…' : 'Analyser la facture'}
          </Button>
        </div>
      </Card>

      <Card title="Factures importées">
        {factures === null ? <Loader fullScreen={false} /> : factures.length === 0 ? (
          <EmptyState icon={LuFileText} title="Aucune facture" text="Les factures analysées apparaîtront ici." />
        ) : (
          <div className="aia-historique">
            {factures.map(f => (
              <button key={f.id} className="aia-histo-ligne" onClick={() => ouvrir(f.id)}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="aia-histo-titre">{f.fournisseur || `Facture #${f.id}`}{f.numero_facture && <span className="aia-sub"> · {f.numero_facture}</span>}</div>
                  <div className="aia-sub">
                    Importée le {dateFr(f.created_at)}{f.utilisateur_prenom ? ` par ${f.utilisateur_prenom}` : ''}
                    {etablissements.length > 1 ? ` · ${f.etablissement_nom}` : ''}
                    {Number(f.nb_lignes) > 0 ? ` · ${f.nb_lignes} article(s), ${f.nb_unites} unité(s)` : ''}
                  </div>
                </div>
                <Badge tone={STATUTS[f.statut]?.tone}>{STATUTS[f.statut]?.label || f.statut}</Badge>
              </button>
            ))}
          </div>
        )}
      </Card>
    </>
  );
}

// ════════════════════════ FACTURE : analyse / vérification ════════════════════════
function Facture({ id, retour, notifier, onStockChange }) {
  const [donnees, setDonnees] = useState(null);
  const [entete, setEntete] = useState(null);
  const [lignes, setLignes] = useState([]);
  const [action, setAction] = useState(null);
  const [coef, setCoef] = useState('2');
  const [confirmer, setConfirmer] = useState(false);
  const initialise = useRef(false);

  const charger = useCallback(async () => {
    try {
      const r = await API.get(`/factures/${id}`);
      setDonnees(r.data);
      if (r.data.facture.statut !== 'analyse' && !initialise.current) {
        initialise.current = true;
        const f = r.data.facture;
        setEntete({
          fournisseur: f.fournisseur || '', numero_facture: f.numero_facture || '',
          taux_change: Number(f.taux_change), frais_port: Number(f.frais_port),
        });
        setLignes(r.data.lignes.map(l => ({
          ...l,
          quantite: l.quantite,
          prix_unitaire: l.prix_unitaire === null ? '' : Number(l.prix_unitaire),
          prix_vente: l.prix_vente === null ? '' : Number(l.prix_vente),
          produit_id: l.produit_id || '',
        })));
      }
    } catch (e) { notifier('❌ ' + msgErreur(e)); }
  }, [id, notifier]);

  useEffect(() => { charger(); }, [charger]);
  const statut = donnees?.facture?.statut;
  useEffect(() => {
    if (statut !== 'analyse') return undefined;
    const t = setInterval(charger, 3000);
    return () => clearInterval(t);
  }, [statut, charger]);

  const { couts, totalDevise } = useMemo(
    () => (entete ? calculerCouts(entete.taux_change, entete.frais_port, lignes) : { couts: {}, totalDevise: 0 }),
    [entete, lignes]
  );

  if (!donnees) return <Loader fullScreen={false} />;
  const f = donnees.facture;
  const produits = donnees.produits || [];
  const lectureSeule = f.statut !== 'a_verifier';
  const devise = f.devise || 'XOF';
  const enFcfa = ['XOF', 'FCFA', 'CFA'].includes(devise.toUpperCase());

  const maj = (lid, champs) => setLignes(ls => ls.map(l => (l.id === lid ? { ...l, ...champs } : l)));
  const gardees = lignes.filter(l => l.action !== 'ignorer');
  const unites = gardees.reduce((s, l) => s + (Number(l.quantite) || 0), 0);
  const totalFcfa = gardees.reduce((s, l) => s + (couts[l.id] || 0) * (Number(l.quantite) || 0), 0);
  const prixManquants = lignes.filter(l => l.action === 'nouveau' && !(Number(l.prix_vente) > 0)).length;
  const produitManquant = lignes.filter(l => l.action === 'existant' && !l.produit_id).length;
  const bloquant = prixManquants + produitManquant;

  const corps = () => ({
    ...entete,
    lignes: lignes.map(l => ({
      id: l.id, action: l.action, produit_id: l.action === 'existant' ? l.produit_id : null,
      nom_propose: l.nom_propose, categorie_proposee: l.categorie_proposee,
      quantite: Number(l.quantite) || 0, prix_unitaire: l.prix_unitaire,
      prix_vente: Number(l.prix_vente) > 0 ? Number(l.prix_vente) : null,
    })),
  });

  const executer = async (nom, fn) => {
    setAction(nom);
    try { await fn(); } catch (e) { notifier('❌ ' + msgErreur(e)); } finally { setAction(null); }
  };
  const brouillon = () => executer('brouillon', async () => {
    const r = await API.put(`/factures/${id}`, corps());
    notifier(r.data.message);
  });
  const valider = () => executer('valider', async () => {
    setConfirmer(false);
    const r = await API.post(`/factures/${id}/valider`, corps());
    notifier(r.data.message);
    onStockChange?.();
    initialise.current = false;
    await charger();
  });
  const relancer = () => executer('relancer', async () => {
    const r = await API.post(`/factures/${id}/relancer`);
    notifier(r.data.message);
    initialise.current = false;
    await charger();
  });
  const abandonner = () => executer('abandon', async () => {
    await API.delete(`/factures/${id}`);
    notifier('✅ Facture abandonnée');
    retour();
  });
  const remplirPrix = () => {
    const c = Number(String(coef).replace(',', '.'));
    if (!(c > 0)) return;
    let n = 0;
    const nouvelles = lignes.map(l => {
      if (l.action !== 'nouveau' || Number(l.prix_vente) > 0 || !couts[l.id]) return l;
      n += 1;
      return { ...l, prix_vente: Math.ceil((couts[l.id] * c) / 100) * 100 };
    });
    setLignes(nouvelles);
    notifier(n ? `✅ ${n} prix proposé(s) — vérifiez-les` : '✅ Aucun prix vide à remplir');
  };

  // ── Analyse en cours / erreur ──
  if (f.statut === 'analyse') {
    return (
      <>
        <Retour retour={retour} />
        <Card>
          <div className="aia-analyse">
            <div className="aia-spinner" aria-hidden />
            <h2 className="aia-titre">Lecture de la facture…</h2>
            <p className="aia-sub">Il extrait chaque article, regarde les photos et cherche les produits déjà en stock. Cela prend en général 30 secondes à 2 minutes. Vous pouvez quitter cette page : l'analyse continue.</p>
          </div>
        </Card>
      </>
    );
  }
  if (f.statut === 'erreur') {
    return (
      <>
        <Retour retour={retour} />
        <Card>
          <EmptyState
            icon={LuTriangleAlert} title="L'analyse a échoué" text={f.erreur}
            action={<div className="aia-actions" style={{ justifyContent: 'center' }}>
              <Button icon={LuRefreshCw} disabled={!!action} onClick={relancer}>Relancer l'analyse</Button>
              <Button variant="danger" icon={LuTrash2} disabled={!!action} onClick={abandonner}>Abandonner</Button>
            </div>}
          />
        </Card>
      </>
    );
  }

  return (
    <>
      <div className="aia-haut">
        <Retour retour={retour} />
        {!lectureSeule && <Button variant="danger" icon={LuTrash2} disabled={!!action} onClick={abandonner}>Abandonner</Button>}
      </div>

      {/* ── En-tête de la facture ── */}
      <Card style={{ marginBottom: 16 }}>
        <div className="aia-entete">
          <div style={{ minWidth: 0 }}>
            <div className="aia-badges">
              <Badge tone={STATUTS[f.statut]?.tone}>{STATUTS[f.statut]?.label}</Badge>
              {f.date_facture && <span className="aia-sub">Facture du {dateFr(f.date_facture)}</span>}
              <span className="aia-sub">{f.etablissement_nom}</span>
            </div>
            {lectureSeule
              ? <h2 className="aia-titre" style={{ marginTop: 8 }}>{f.fournisseur || `Facture #${f.id}`}{f.numero_facture ? ` · ${f.numero_facture}` : ''}</h2>
              : (
                <div className="aia-grille" style={{ marginTop: 10 }}>
                  <label className="aia-champ"><span>Fournisseur</span>
                    <input className="aia-input" value={entete.fournisseur} onChange={e => setEntete({ ...entete, fournisseur: e.target.value })} />
                  </label>
                  <label className="aia-champ"><span>N° de facture</span>
                    <input className="aia-input" value={entete.numero_facture} onChange={e => setEntete({ ...entete, numero_facture: e.target.value })} />
                  </label>
                  {!enFcfa && (
                    <label className="aia-champ"><span>Taux : 1 {devise} = ? FCFA</span>
                      <input className="aia-input" type="number" min="0" step="any" inputMode="decimal" value={entete.taux_change} onChange={e => setEntete({ ...entete, taux_change: e.target.value })} />
                    </label>
                  )}
                  <label className="aia-champ"><span>Frais de port ({devise})</span>
                    <input className="aia-input" type="number" min="0" step="any" inputMode="decimal" value={entete.frais_port} onChange={e => setEntete({ ...entete, frais_port: e.target.value })} />
                  </label>
                </div>
              )}
          </div>
          <dl className="aia-resume">
            <div><dt>Articles</dt><dd>{gardees.length}<span className="aia-sub"> / {lignes.length}</span></dd></div>
            <div><dt>Unités</dt><dd>{fmt(unites)}</dd></div>
            <div><dt>Total {enFcfa ? '' : devise}</dt><dd>{enFcfa ? fmtF(totalDevise) : totalDevise.toLocaleString('fr-FR', { maximumFractionDigits: 2 })}</dd></div>
            {!enFcfa && <div><dt>Coût en FCFA</dt><dd>{fmtF(totalFcfa)}</dd></div>}
          </dl>
        </div>
        {f.total_facture && Math.abs(Number(f.total_facture) - (totalDevise + Number(entete?.frais_port || f.frais_port || 0))) > 1 && !lectureSeule && (
          <div className="aia-alerte"><LuTriangleAlert aria-hidden /> Le total de la facture ({Number(f.total_facture).toLocaleString('fr-FR')} {devise}) ne correspond pas à la somme des lignes gardées + frais de port. Vérifiez les quantités et les lignes ignorées.</div>
        )}
        {f.remarques_ia && <div className="aia-alerte is-info"><LuInfo aria-hidden /> {f.remarques_ia}</div>}
      </Card>

      {/* ── Outil prix de vente ── */}
      {!lectureSeule && (
        <div className="aia-outil">
          <span>Prix de vente vides : coût d'achat ×</span>
          <input className="aia-input aia-input-court" type="number" min="1" step="0.1" inputMode="decimal" value={coef} onChange={e => setCoef(e.target.value)} aria-label="Coefficient" />
          <Button variant="secondary" onClick={remplirPrix}>Proposer</Button>
          <span className="aia-sub">(arrondi à 100 F ; chaque prix reste modifiable)</span>
        </div>
      )}

      {/* ── Lignes ── */}
      <div className="aia-lignes">
        {lignes.map(l => {
          const cout = couts[l.id];
          const produit = produits.find(p => p.id === Number(l.produit_id));
          const marge = l.action === 'nouveau' && cout && Number(l.prix_vente) > 0 ? Math.round(((l.prix_vente - cout) / l.prix_vente) * 100) : null;
          return (
            <article key={l.id} className={`aia-ligne is-${l.action}`}>
              <div className="aia-ligne-photo">
                {l.image ? <img src={urlUpload(l.image)} alt={l.nom_propose || `Article ${l.numero_ligne}`} loading="lazy" /> : <LuImage aria-hidden />}
                <span className="aia-ligne-num">{l.numero_ligne}</span>
              </div>

              <div className="aia-ligne-corps">
                <p className="aia-ligne-desc">{l.description || 'Sans description'}</p>
                {l.tailles && <p className="aia-sub">Tailles : {l.tailles}</p>}

                <div className="aia-chiffres">
                  <label className="aia-champ"><span>Quantité</span>
                    {lectureSeule ? <strong>{l.quantite}</strong>
                      : <input className="aia-input" type="number" min="0" inputMode="numeric" value={l.quantite} onChange={e => maj(l.id, { quantite: e.target.value })} />}
                  </label>
                  <label className="aia-champ"><span>Prix unitaire ({devise})</span>
                    {lectureSeule ? <strong>{Number(l.prix_unitaire).toLocaleString('fr-FR')}</strong>
                      : <input className="aia-input" type="number" min="0" step="any" inputMode="decimal" value={l.prix_unitaire} onChange={e => maj(l.id, { prix_unitaire: e.target.value })} />}
                  </label>
                  <div className="aia-champ"><span>Coût de revient</span><strong className="aia-num">{cout ? fmtF(cout) : '—'}</strong></div>
                </div>

                {lectureSeule ? (
                  <p className="aia-resultat">
                    {l.action === 'ignorer' ? 'Ignoré'
                      : <><LuCircleCheck aria-hidden /> {l.action === 'nouveau' ? 'Créé' : 'Ajouté à'} : <strong>{l.produit_nom || l.nom_propose}</strong></>}
                  </p>
                ) : (
                  <>
                    <div className="aia-segment" role="radiogroup" aria-label="Que faire de cet article ?">
                      {ACTIONS.map(a => (
                        <button key={a.id} role="radio" aria-checked={l.action === a.id} onClick={() => maj(l.id, { action: a.id })}>{a.label}</button>
                      ))}
                    </div>

                    {l.action === 'nouveau' && (
                      <div className="aia-grille">
                        <label className="aia-champ aia-large"><span>Nom du produit</span>
                          <input className="aia-input" value={l.nom_propose || ''} onChange={e => maj(l.id, { nom_propose: e.target.value })} />
                        </label>
                        <label className="aia-champ"><span>Catégorie</span>
                          <input className="aia-input" value={l.categorie_proposee || ''} onChange={e => maj(l.id, { categorie_proposee: e.target.value })} />
                        </label>
                        <label className="aia-champ"><span>Prix de vente (FCFA) *</span>
                          <input
                            className={`aia-input ${!(Number(l.prix_vente) > 0) ? 'is-vide' : ''}`} type="number" min="0" inputMode="numeric"
                            value={l.prix_vente} placeholder="À saisir" onChange={e => maj(l.id, { prix_vente: e.target.value })}
                          />
                          {marge !== null && <small className={marge < 15 ? 'aia-marge is-faible' : 'aia-marge'}>Marge {marge} %</small>}
                        </label>
                      </div>
                    )}

                    {l.action === 'existant' && (
                      <div className="aia-grille">
                        <label className="aia-champ aia-large"><span>Produit en stock {l.confiance && <Badge tone={l.confiance === 'haute' ? 'success' : l.confiance === 'moyenne' ? 'warn' : 'danger'}>{l.confiance === 'haute' ? 'Correspondance sûre' : l.confiance === 'moyenne' ? 'Correspondance probable' : 'À vérifier'}</Badge>}</span>
                          <select className={`aia-input ${!l.produit_id ? 'is-vide' : ''}`} value={l.produit_id} onChange={e => maj(l.id, { produit_id: e.target.value, confiance: null })}>
                            <option value="">— Choisir le produit —</option>
                            {produits.map(p => <option key={p.id} value={p.id}>{p.nom}{p.categorie ? ` (${p.categorie})` : ''} — stock {p.quantite}</option>)}
                          </select>
                        </label>
                        <label className="aia-champ"><span>Nouveau prix de vente</span>
                          <input className="aia-input" type="number" min="0" inputMode="numeric" value={l.prix_vente}
                            placeholder={produit ? `Actuel : ${fmt(produit.prix)}` : 'Inchangé'} onChange={e => maj(l.id, { prix_vente: e.target.value })} />
                        </label>
                        {produit && <p className="aia-sub aia-large">Stock actuel {produit.quantite} → <strong>{produit.quantite + (Number(l.quantite) || 0)}</strong> après validation</p>}
                      </div>
                    )}
                  </>
                )}
              </div>
            </article>
          );
        })}
      </div>

      {/* ── Barre de validation ── */}
      {!lectureSeule && (
        <div className="aia-barre">
          <div className="aia-barre-info">
            {bloquant > 0
              ? <span className="aia-a-faire"><LuTriangleAlert aria-hidden /> {prixManquants > 0 && `${prixManquants} prix de vente à saisir`}{prixManquants > 0 && produitManquant > 0 && ' · '}{produitManquant > 0 && `${produitManquant} produit(s) à choisir`}</span>
              : <span><LuCircleCheck aria-hidden /> Prêt : {fmt(unites)} unités sur {gardees.length} article(s)</span>}
          </div>
          <div className="aia-actions">
            <Button variant="secondary" icon={LuSave} disabled={!!action} onClick={brouillon}>Enregistrer</Button>
            {confirmer ? (
              <>
                <Button icon={LuPackagePlus} disabled={!!action} onClick={valider}>Confirmer la mise à jour du stock</Button>
                <Button variant="secondary" onClick={() => setConfirmer(false)}>Retour</Button>
              </>
            ) : (
              <Button icon={LuPackagePlus} disabled={!!action || bloquant > 0 || gardees.length === 0} onClick={() => setConfirmer(true)}>Valider et approvisionner</Button>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function Retour({ retour }) {
  return (
    <button className="aia-retour" onClick={retour}><LuArrowLeft aria-hidden /> Toutes les factures</button>
  );
}

